import React, {useContext, useState} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

import sy from '~/styles';

import {useDispatch} from '~/lib/hooks';
import {
  setShowDrawer,
  showLogoutDialog,
  showSettingsModal,
  showHelpModal,
} from '~/actions';
import {
  Text,
  Pressable,
  PrimaryButton,
  Drawer,
  DealerPersona,
  PrivateText,
} from '~/components/controls';
import {
  DrawerHeader,
  DrawerTitle,
  DrawerSubtitle,
  DrawerContent,
} from '~/components/controls/Drawer';
import {user as userApi} from '~/api/private';
import UserContext from '~/components/context/UserContext';
import withDimensions from '~/components/hoc/with-dimensions';

import CheckboxIcon from '../../../images/md-icons/check_box/materialicons/24px.svg';
import CheckboxOutlineIcon from '../../../images/md-icons/check_box_outline_blank/materialicons/24px.svg';
import IndeterminateCheckboxIcon from '~/images/md-icons/indeterminate_check_box/materialicons/24px.svg';
import SettingsIcon from '~/images/md-icons/settings/materialsymbolsoutlined/24px.svg';
import LogoutIcon from '~/images/md-icons/logout_white/materialicons/18px.svg';
import HelpCenterIcon from '~/images/md-icons/help_center/materialsymbolsoutlined/24px.svg';
import ExpandLessIcon from '~/images/md-icons/expand_less/materialicons/24px.svg';
import ExpandMoreIcon from '~/images/md-icons/expand_more/materialicons/24px.svg';
import PropTypes from 'prop-types';

const getCityDealerIds = (cityDealers) =>
  cityDealers.map((dealer) => dealer.id);

const getCitySelectionState = (cityDealers, selectedDealerIds) => {
  const cityDealerIds = new Set(getCityDealerIds(cityDealers));
  let selectedCount = 0;

  for (const id of selectedDealerIds) {
    if (cityDealerIds.has(id)) {
      selectedCount += 1;
    }
  }

  return {
    all: selectedCount > 0 && selectedCount === cityDealers.length,
    some: selectedCount > 0 && selectedCount < cityDealers.length,
  };
};

const getSelectionWithoutCity = (selectedDealerIds, cityDealers) => {
  const cityDealerIds = new Set(getCityDealerIds(cityDealers));
  return selectedDealerIds.filter((id) => !cityDealerIds.has(id));
};

const mergeCitySelection = (selectedDealerIds, cityDealers) => [
  ...new Set([...selectedDealerIds, ...getCityDealerIds(cityDealers)]),
];

const toggleExpandedCity = (expandedCities, city) =>
  expandedCities.includes(city)
    ? expandedCities.filter((item) => item !== city)
    : [...expandedCities, city];

const toggleDealerSelection = (selectedDealerIds, dealerId) =>
  selectedDealerIds.includes(dealerId)
    ? selectedDealerIds.filter((id) => id !== dealerId)
    : [...selectedDealerIds, dealerId];

const renderAllDealersIcon = (dealerSelectionCount, dealerCount) => {
  if (dealerSelectionCount === dealerCount) {
    return <CheckboxIcon fill="#231fda" />;
  }

  if (dealerSelectionCount === 0) {
    return <CheckboxOutlineIcon fill="#4a4a49" />;
  }

  return <IndeterminateCheckboxIcon fill="#231fda" />;
};

const renderCitySelectionIcon = (all, some) => {
  if (all) {
    return <CheckboxIcon fill="#231fda" />;
  }

  if (some) {
    return <IndeterminateCheckboxIcon fill="#231fda" />;
  }

  return <CheckboxOutlineIcon fill="#4a4a49" />;
};

const SideDrawer = ({dimensions}) => {
  const dispatch = useDispatch();
  const {top} = useSafeAreaInsets();

  const {me, update} = useContext(UserContext);

  const {width} = dimensions.window;

  const cities = me?.dealers?.reduce((acc, dealer) => {
    acc[dealer.city] = acc[dealer.city] || [];
    acc[dealer.city].push(dealer);
    return acc;
  }, {});

  const [expanded, setExpanded] = useState([]);

  return (
    <Drawer
      onClose={() => dispatch(setShowDrawer(false))}
      style={{
        width: Math.max(Math.min(width - 56, 356), 256),
        paddingBottom: top,
      }}>
      <DrawerHeader style={{paddingBottom: 16}}>
        <DrawerTitle>
          <PrivateText style={sy.largePlus}>
            {me?.first_name} {me?.last_name}
          </PrivateText>
        </DrawerTitle>
        <DrawerSubtitle>
          <PrivateText style={[sy.smallPlus, {color: '#828282'}]}>
            {me?.email}
          </PrivateText>
        </DrawerSubtitle>
      </DrawerHeader>
      <DrawerContent>
        <Divider style={sy['bg-gray95']} />
        <ScrollView style={sy['bg-gray95']}>
          <View>
            <Text
              style={[
                sy['p-4'],
                sy['bg-white'],
                sy.smallPlus,
                {color: '#828282'},
              ]}>
              Vestiging
            </Text>
            <View>
              {me?.dealers?.length > 1 && (
                <View
                  style={[
                    sy['p-4'],
                    sy['bg-white'],
                    sy['flex-row'],
                    sy['items-center'],
                    sy['gap-4'],
                  ]}>
                  <Pressable
                    onPress={async () => {
                      let changes = [];
                      if (me.dealers.length !== me.dealer_selection.length) {
                        changes = me.dealers.map(({id}) => id);
                      }

                      await userApi.update({dealer_selection: changes});
                      await update();

                      if (changes.length === 0) {
                        setExpanded([]);
                      }
                    }}>
                    {renderAllDealersIcon(
                      me.dealer_selection.length,
                      me.dealers.length,
                    )}
                  </Pressable>

                  <Text>Alle</Text>
                </View>
              )}

              {Object.keys(cities ?? {})
                .sort((a, b) => a.localeCompare(b))
                .map((city) => {
                  const cityDealers = cities[city];
                  const {all, some} = getCitySelectionState(
                    cityDealers,
                    me.dealer_selection,
                  );

                  return (
                    <React.Fragment key={city}>
                      <View
                        style={[
                          sy['p-4'],
                          sy['bg-white'],
                          sy['flex-row'],
                          sy['items-center'],
                          sy['gap-4'],
                          me?.dealers?.length > 1 ? sy['pl-6'] : null,
                        ]}>
                        <Pressable
                          onPress={async () => {
                            const changes = all
                              ? getSelectionWithoutCity(
                                  me.dealer_selection,
                                  cityDealers,
                                )
                              : mergeCitySelection(
                                  me.dealer_selection,
                                  cityDealers,
                                );

                            await userApi.update({
                              dealer_selection: changes,
                            });
                            await update();
                          }}>
                          {renderCitySelectionIcon(all, some)}
                        </Pressable>
                        <View style={[sy['flex-1']]}>
                          <Text>{city}</Text>
                        </View>
                        <Pressable
                          onPress={() => {
                            setExpanded(toggleExpandedCity(expanded, city));
                          }}>
                          {expanded.includes(city) ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </Pressable>
                      </View>
                      {expanded.includes(city) &&
                        cityDealers.map((dealer) => (
                          <View
                            key={dealer.id}
                            style={[
                              sy['p-4'],
                              sy['bg-white'],
                              sy['flex-row'],
                              sy['items-center'],
                              sy['gap-4'],
                              me?.dealers?.length > 1
                                ? sy['pl-12']
                                : sy['pl-6'],
                            ]}>
                            <Pressable
                              onPress={async () => {
                                const changes = toggleDealerSelection(
                                  me.dealer_selection,
                                  dealer.id,
                                );

                                await userApi.update({
                                  dealer_selection: changes,
                                });
                                await update();
                              }}>
                              {me?.dealer_selection?.includes(dealer.id) ? (
                                <CheckboxIcon fill="#231fda" />
                              ) : (
                                <CheckboxOutlineIcon fill="#4a4a49" />
                              )}
                            </Pressable>
                            <View style={[sy['flex-1']]}>
                              <Text>{dealer.short_name}</Text>
                            </View>
                            <DealerPersona dealer_id={dealer.id} size={18} />
                          </View>
                        ))}
                    </React.Fragment>
                  );
                })}
            </View>
          </View>
        </ScrollView>
        <Divider style={sy['bg-gray95']} />
        <View style={[sy['p-4']]}>
          <Pressable
            onPress={() => {
              dispatch(setShowDrawer(false));
              dispatch(showSettingsModal());
            }}
            style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
            <SettingsIcon fill="#4a4a49" />
            <Text>Instellingen</Text>
          </Pressable>
        </View>
        <Divider style={sy['bg-gray95']} />
        <View style={[sy['p-4']]}>
          <Pressable
            onPress={() => {
              dispatch(setShowDrawer(false));
              dispatch(showHelpModal());
            }}
            style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
            <HelpCenterIcon fill="#4a4a49" />
            <Text>Support</Text>
          </Pressable>
        </View>
      </DrawerContent>
      <Divider style={sy['bg-gray88']} />
      <View style={[sy['p-4'], sy['justify-center'], sy['items-center']]}>
        <View style={{width: 160}}>
          <PrimaryButton
            icon={<LogoutIcon />}
            onPress={() => {
              dispatch(setShowDrawer(false));
              dispatch(showLogoutDialog());
            }}>
            Uitloggen
          </PrimaryButton>
        </View>
      </View>
    </Drawer>
  );
};
SideDrawer.propTypes = {
  dimensions: PropTypes.any,
};

export default withDimensions(SideDrawer);
